import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Loader } from '../shared/component';
import { InconsistenciesList } from './components';
import { ValidationProcess, ValidationRuleType } from './types';

export type InconsistenciesResponseType = {
  isLoading: boolean;
  values: { [key: string]: ValidationRuleType[] };
};

enum TABS {
  SUBMISSION,
  APPROVAL,
  NONE,
}

export const Inconsistencies: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isLoading, values: rules } = useSelector((state: any): InconsistenciesResponseType => state.validationRules ?? {});

  const { t } = useTranslation(['translation']);

  const [activeTab, setActiveTab] = useState(TABS.NONE);

  const submissionRules = useMemo(() => rules[ValidationProcess.SUBMISSION] || [], [rules]);
  const approvalRules = useMemo(() => rules[ValidationProcess.APPROVAL] || [], [rules]);

  const submissionErrors = useMemo(() => {
    return submissionRules.filter((rule) => rule.validationRuleLevel === 'ERROR').length;
  }, [submissionRules]);

  const submissionWarnings = useMemo(() => {
    return submissionRules.filter((rule) => rule.validationRuleLevel === 'WARNING').length;
  }, [submissionRules]);

  const approvalErrors = useMemo(() => {
    return approvalRules.filter((rule) => rule.validationRuleLevel === 'ERROR').length;
  }, [approvalRules]);

  const approvalWarnings = useMemo(() => {
    return approvalRules.filter((rule) => rule.validationRuleLevel === 'WARNING').length;
  }, [approvalRules]);

  useEffect(() => {
    if (activeTab === TABS.SUBMISSION && submissionRules.length === 0) {
      if (approvalRules.length > 0) {
        setActiveTab(TABS.APPROVAL);
      }
    } else if (activeTab === TABS.APPROVAL && approvalRules.length === 0) {
      if (submissionRules.length > 0) {
        setActiveTab(TABS.SUBMISSION);
      }
    } else if (activeTab === TABS.NONE) {
      if (submissionRules.length > 0) {
        setActiveTab(TABS.SUBMISSION);
      } else if (approvalRules.length > 0) {
        setActiveTab(TABS.APPROVAL);
      }
    }
  }, [submissionRules, approvalRules, activeTab]);

  return (
    <div className="inconsistencies-menu">
      <div className="inconsistencies-tabs">
        <div
          className={`inconsistencies-tab${activeTab === TABS.SUBMISSION && submissionRules.length !== 0 ? ' active' : ' disabled'}`}
          onClick={() => setActiveTab(TABS.SUBMISSION)}
        >
          {t('enums.validationRule.menu.submission')}
          <span className="inconsistencies-tab-badges">
            {submissionRules.length > 0 && (
              <>
                <Badge bg="danger">{submissionErrors}</Badge>
                <Badge className="ms-1" bg="warning">
                  {submissionWarnings}
                </Badge>
              </>
            )}
            {submissionRules.length === 0 && <FontAwesomeIcon icon={faCheck} size="sm" />}
          </span>
        </div>
        <div
          className={`inconsistencies-tab${activeTab === TABS.APPROVAL && approvalRules.length !== 0 ? ' active' : ' disabled'}`}
          onClick={() => setActiveTab(TABS.APPROVAL)}
        >
          {t('enums.validationRule.menu.approval')}
          <span className="inconsistencies-tab-badges">
            {approvalRules.length > 0 && (
              <>
                <Badge bg="danger">{approvalErrors}</Badge>
                <Badge className="ms-1" bg="warning">
                  {approvalWarnings}
                </Badge>
              </>
            )}
            {approvalRules.length === 0 && <FontAwesomeIcon icon={faCheck} size="sm" />}
          </span>
        </div>
      </div>
      {isLoading && (
        <div className="inconsistency-loader loader">
          <Loader size="2x" />
        </div>
      )}
      {activeTab === TABS.SUBMISSION && <InconsistenciesList rules={submissionRules} />}
      {activeTab === TABS.APPROVAL && <InconsistenciesList rules={approvalRules} />}
    </div>
  );
};
